import { Link } from "react-router-dom";
import {
  Wrench, Headphones, ShoppingCart, Database, Shield,
  Activity, AlertTriangle, Server, Zap, ArrowRight, Check
} from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/JsonLd";

const services = [
  {
    icon: Wrench,
    title: "Mentenanță WordPress",
    text: "Ținem site-ul actualizat, monitorizat și funcțional printr-un proces clar de actualizări, verificări și intervenții recurente.",
  },
  {
    icon: Headphones,
    title: "Suport WordPress",
    text: "Când apare o problemă, răspundem clar și intervenim organizat, fie că vorbim despre erori, incidente sau ajustări minore.",
  },
  {
    icon: ShoppingCart,
    title: "WooCommerce Care",
    text: "Pentru magazine online care au nevoie de mai multă atenție, verificări suplimentare și prioritate crescută atunci când sunt afectate comenzile sau checkout-ul.",
  },
  {
    icon: Database,
    title: "Backup și restaurare",
    text: "Configurăm și păstrăm backup-uri recurente, cu retenție adaptată tipului de site, astfel încât restaurarea să fie posibilă atunci când contează.",
  },
  {
    icon: Shield,
    title: "Securitate și hardening",
    text: "Reducem expunerea la riscuri comune prin verificări malware, bune practici de securitate și o abordare mai controlată a mentenanței.",
  },
  {
    icon: Activity,
    title: "Monitorizare și uptime",
    text: "Monitorizăm disponibilitatea site-ului și reacționăm atunci când apar probleme, pentru a reduce riscul perioadelor lungi de indisponibilitate.",
  },
];

const infraHighlights = [
  "Infrastructură potrivită pentru site-uri business și WooCommerce",
  "Resurse mai bine controlate",
  "Mai puține limitări tipice hostingului low-cost",
  "Setup mai sănătos pentru stabilitate și trafic",
];

const perfHighlights = [
  "Verificări de bază pentru performanță",
  "Setup corect de cache și bune practici tehnice",
  "Recomandări pentru stabilitate și viteză",
  "Optimizări avansate pentru proiectele mai complexe",
  "Configurări orientate spre WordPress și WooCommerce",
];

const auditChecks = [
  "Starea generală a site-ului",
  "Update-uri și riscuri tehnice",
  "Backup și securitate",
  "Hosting și infrastructură",
  "Performanță de bază",
  "Oportunități de optimizare",
  "Potrivirea cu serviciul de mentenanță și hosting",
];

export default function ServicesPage() {
  usePageMeta(
    "Servicii WordPress - Ordell WebCare",
    "Mentenanță, suport, WooCommerce care, backup, securitate, monitorizare uptime, intervenții și optimizare performanță pentru site-uri WordPress."
  );

  return (
    <>
      <JsonLd page="servicii" />
      {/* Hero */}
      <section data-testid="services-hero" className="py-16 lg:py-24">
        <div className="section-container max-w-3xl">
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold text-[#1A1D20] mb-6">
            Servicii WordPress construite pentru stabilitate, suport și continuitate
          </h1>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-8">
            De la mentenanță recurentă și backup-uri până la WooCommerce, securitate, infrastructură și optimizări de performanță, Ordell WebCare este construit pentru business-uri care au nevoie de mai mult decât simple update-uri.
          </p>
          <Link
            to="/contact"
            data-testid="services-hero-cta"
            className="inline-flex items-center gap-2 bg-[#0F2942] text-white font-medium px-6 py-3 rounded-xl hover:bg-[#0F2942]/90 transition-all"
          >
            Cere auditul tehnic inițial <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Intro */}
      <section className="pb-12 lg:pb-16">
        <div className="section-container max-w-3xl">
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed">
            Site-ul tău nu are nevoie doar de intervenții ocazionale. Are nevoie de un proces clar de mentenanță, monitorizare, backup, securitate și suport, astfel încât să rămână funcțional, stabil și pregătit pentru business.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="services-grid" className="pb-20 lg:pb-28">
        <div className="section-container">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20] mb-10">
            Serviciile noastre
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <div
                key={i}
                data-testid={`service-card-${i}`}
                className="bg-white rounded-2xl p-8 border border-[#0F2942]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all hover:-translate-y-1"
              >
                <div className="w-10 h-10 rounded-xl bg-[#F0F5F4] flex items-center justify-center mb-5">
                  <service.icon className="w-5 h-5 text-[#1A4A40]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-medium text-[#1A1D20] mb-3">{service.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{service.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interventii */}
      <section className="py-16 lg:py-20 bg-[#F1F3F5]">
        <div className="section-container max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-[#0F2942]/10">
              <div className="w-10 h-10 rounded-xl bg-[#F0F5F4] flex items-center justify-center mb-5">
                <AlertTriangle className="w-5 h-5 text-[#1A4A40]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-medium text-[#1A1D20] mb-3">Intervenții și remedieri</h3>
              <p className="text-sm text-[#4A5568] leading-relaxed">
                De la erori apărute după update-uri până la probleme de funcționare, intervenim organizat și documentat, nu improvizat.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-[#0F2942]/10">
              <div className="w-10 h-10 rounded-xl bg-[#F0F5F4] flex items-center justify-center mb-5">
                <Zap className="w-5 h-5 text-[#1A4A40]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-medium text-[#1A1D20] mb-3">Optimizări de performanță</h3>
              <p className="text-sm text-[#4A5568] leading-relaxed">
                Încărcare mai rapidă, caching corect și recomandări tehnice utile.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section data-testid="services-infra" className="py-20 lg:py-28 bg-[#0A1118] text-white">
        <div className="section-container max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-5">
                <Server className="w-5 h-5 text-[#4A9E96]" />
              </div>
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight font-semibold mb-4">
                Infrastructură mai potrivită pentru proiectele unde continuitatea contează
              </h2>
              <p className="text-white/70 leading-relaxed">
                Pentru proiectele care au nevoie de mai multă predictibilitate, folosim infrastructură managed sau dedicată, configurată pentru stabilitate, trafic și continuitate. Evităm limitările tipice mediilor shared aglomerate atunci când proiectul cere mai mult control și mai multă siguranță operațională.
              </p>
            </div>
            <div className="space-y-3">
              {infraHighlights.map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-white/10">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-white/80">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Performance */}
      <section data-testid="services-perf" className="py-20 lg:py-28">
        <div className="section-container max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="w-10 h-10 rounded-xl bg-[#F0F5F4] flex items-center justify-center mb-5">
                <Zap className="w-5 h-5 text-[#1A4A40]" />
              </div>
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20] mb-4">
                Optimizări pentru o încărcare mai rapidă și o funcționare mai eficientă
              </h2>
              <p className="text-[#4A5568] leading-relaxed">
                În toate planurile includem verificări de bază și bune practici pentru stabilitate și performanță. Pentru proiectele care au nevoie de mai mult, oferim optimizări dedicate pentru timp de răspuns mai bun, caching corect, încărcare mai rapidă și o experiență mai fluidă pentru utilizatori.
              </p>
            </div>
            <div className="space-y-3">
              {perfHighlights.map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#F0F5F4]/50 rounded-xl p-4">
                  <Check className="w-4 h-4 text-[#1A4A40] mt-0.5 shrink-0" />
                  <span className="text-sm text-[#1A1D20]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Audit */}
      <section data-testid="services-audit" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container max-w-3xl mx-auto text-center">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20] mb-4">
            Auditul tehnic inițial îți arată unde există riscuri și ce poate fi îmbunătățit
          </h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-8 text-left max-w-xl mx-auto">
            {auditChecks.map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-[#1A4A40] mt-0.5 shrink-0" />
                <span className="text-sm text-[#1A1D20]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="services-cta" className="py-20 lg:py-28 bg-[#0A1118] text-white">
        <div className="section-container text-center max-w-3xl mx-auto">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold mb-4">
            Mai mult decât mentenanță. Mai mult control asupra unui site important pentru business.
          </h2>
          <p className="text-white/70 leading-relaxed mb-8">
            Ordell WebCare combină mentenanța WordPress, suportul, infrastructura și performanța într-un serviciu construit pentru companii care vor mai puțin stres, mai puține blocaje și mai multă liniște operațională.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-btn"
            className="inline-flex items-center gap-2 bg-white text-[#0F2942] font-medium px-8 py-3.5 rounded-xl hover:bg-white/90 transition-all"
          >
            Cere auditul tehnic inițial <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
